//@ts-nocheck
import { useEffect, useState, useRef } from 'react';
import clsx from 'clsx';
import { getSignedUrl } from '../../utils/useSignedUrl';
import Slider from '../atoms/slider';
import SectionName from '../atoms/section-name';
import BannerHeadline from '../atoms/banner-headline';
import Description from '../atoms/description';
import CTABtn from '../atoms/cta-btn';
import VideoEmbed from '../atoms/video-embed';
import { CXAHubInterpreter } from '../../utils/cxa-hub-interpreter';
import { slugBuilder } from '../../utils/slug-builder';
import DOMPurify from "dompurify";
import { useTheme } from '../../utils/theme';
import { GrNext, GrPrevious } from "react-icons/gr";
import { GoDotFill } from 'react-icons/go';
import styles from './hero.module.css';
import { imageInterpreter } from '../../utils/images-interpreter';

const Hero = ({ title, sectionName, images, description, button = null, children, mode = "normal", announcements = null, cxa_hub = null }) => {
    // console.log("Hero component received props:",  announcements);
    const sanitizeHTML = (html) => DOMPurify.sanitize(html || "", { ADD_TAGS: ["ccpa", "embedVideo", "ccpaform"] });
    const [slides, setSlides] = useState([]);
    const [isMobile, setIsMobile] = useState(false);
    const { theme } = useTheme();
    const lightMode = theme === "light";
    const [embedMedia, setEmbedMedia] = useState(null);
    const imageList = imageInterpreter(images);
    const videoPresent = imageList.some(file => {
        try {
            const cleanUrl = file.split('?')[0];
            const extension = cleanUrl.split('.').pop()?.toLowerCase();
            return ['mp4', 'avi', 'mov', 'webm'].includes(extension || '');
        } catch {
            return false;
        }
    });
    const checkScreenSize = () => setIsMobile(window.innerWidth <= 768);
    useEffect(() => {
        checkScreenSize();
        window.addEventListener('resize', checkScreenSize);
        return () => window.removeEventListener('resize', checkScreenSize);
    }, []);

    // Build slides (hero + announcements)
    useEffect(() => {
        const buildSlides = async () => {
            const heroSlide = {
                sectionName,
                title,
                description,
                button,
                images: images || [],
                videoPresent,
            };
            const announcementSlides = (announcements?.announcements || []).map(a => {
                const ann = a?.attributes ?? a;
                return {
                    sectionName: ann.sectionName,
                    title: ann.title,
                    description: ann.description,
                    button: ann.buttonName ? {
                        buttonName: ann.buttonName,
                        buttonURL: ann.buttonURL,
                        newTab: ann.newTab
                    } : null,
                    images: ann.images?.data?.map(img => img?.attributes?.url) || [],
                    videoPresent: ann.images?.data?.some(file => {
                        try {
                            const cleanUrl = file.attributes.url.split('?')[0];
                            const extension = cleanUrl.split('.').pop()?.toLowerCase();
                            return ['mp4', 'avi', 'mov', 'webm'].includes(extension || '');
                        } catch { return false; }
                    }),
                };
            });

            // Sign URLs
            const allSlides = [heroSlide, ...announcementSlides];
            // console.log("allSlides ", allSlides);

            const signedSlides = await Promise.all(
                allSlides.map(async (s) => {
                    const rawImages =
                        Array.isArray(s.images?.data)
                            ? s.images.data.map(img => img?.attributes?.url).filter(Boolean)
                            : Array.isArray(s.images)
                                ? s.images
                                : [];

                    const signedImages = await Promise.all(
                        rawImages.map((url) => getSignedUrl(url))
                    );

                    return {
                        ...s,
                        images: signedImages,
                    };
                })
            );
            // console.log("buildSlides ", signedSlides);
            setSlides(signedSlides);
        };
        buildSlides();
    }, []);

    // Interpret CXAHub if present
    useEffect(() => {
        if (cxa_hub?.data) setEmbedMedia(CXAHubInterpreter(cxa_hub.data));
    }, [cxa_hub]);

    const [currentIndex, setCurrentIndex] = useState(0);
    const [nextIndex, setNextIndex] = useState(1);
    const [animating, setAnimating] = useState(false);
    const [lastDirection, setLastDirection] = useState("next");
    const intervalRef = useRef(null);
    const isManualChangeRef = useRef(false);

    // Update next index on slide change
    useEffect(() => {
        setNextIndex((currentIndex + 1) % slides.length);
    }, [currentIndex, slides.length]);

    const startInterval = (delay = 7000) => {
        clearInterval(intervalRef.current);
        intervalRef.current = setInterval(() => {
            setAnimating(true);
            setTimeout(() => {
                setCurrentIndex(prev => {
                    const next = (prev + 1) % slides.length;
                    setNextIndex((next + 1) % slides.length);
                    return next;
                });
                setAnimating(false);
            }, isMobile ? 1500 : 1800);
        }, delay);
    };

    useEffect(() => {
        if (slides.length > 1) startInterval();
        return () => clearInterval(intervalRef.current);
    }, [slides.length]);

    const handleManualChange = (direction) => {
        if (animating) return;
        setAnimating(true);
        setLastDirection(direction);
        setTimeout(() => {
            setCurrentIndex(prev => {
                const next = direction === 'next' ? (prev + 1) % slides.length : (prev - 1 + slides.length) % slides.length;
                setNextIndex(direction === 'next' ? (next + 1) % slides.length : (next - 1 + slides.length) % slides.length);
                return next;
            });
            setAnimating(false);
            setLastDirection("next");
        }, isMobile ? 1500 : 1800);
        isManualChangeRef.current = true;
        startInterval(10000);
        setTimeout(() => { if (isManualChangeRef.current) { isManualChangeRef.current = false; startInterval(7000); } }, 10000);
    };
    // console.log(slides);
    const isPassionSlide = (s) => typeof s?.title === 'string' && s.title.toLowerCase().includes('passion for people');

    const renderSlide = (slide, index = 0, isNext = false) => (
        <div className={clsx(
            styles['hero-section'],
            { [styles["overflow"]]: mode === "normal" && !slide?.videoPresent },
            { [styles["non-filter"]]: mode !== "filter" },
            { [styles["movie-mode"]]: animating },
            { [styles["left"]]: lastDirection === "next" },
            { [styles["right"]]: lastDirection === "prev" },
        )}
            style={{
                "--position-animation": lastDirection === "prev" ? "right" : "left"
            }}
        >

            <div className={clsx(
                styles['hero-text'],
                { [styles["non-filter"]]: mode !== "filter" },
                { [styles["light-mode"]]: lightMode },
            )}>
                <div>
                    <SectionName sectionName={slide?.sectionName} dynamic={false} />
                    <BannerHeadline title={slide?.title} width={isMobile ? 80 : (embedMedia ? 100 : 60)} useCase={index === 0 ? "hero" : "other"} />
                    {embedMedia && !isMobile && (<Description description={slide?.description} width={isMobile ? 80 : (embedMedia ? 100 : 50)} />)}
                    {(isPassionSlide(slide) || announcements) && !isMobile && slide?.description && (
                        <div
                            className={clsx(
                                styles.desc
                            )}
                            style={{ width: `${embedMedia ? 100 : 50}%` }}
                            dangerouslySetInnerHTML={{
                                __html: sanitizeHTML(slide.description),
                            }}
                        />
                    )}
                    {embedMedia && isMobile && (
                        <div className={styles.media}>
                            <VideoEmbed view="view" url={embedMedia.embedMedia} ctaRedirectURL={`/${slugBuilder("what we think", "CXA Hub", embedMedia?.title)}`} />
                        </div>
                    )}
                    {embedMedia && isMobile && (
                        <Description description={slide?.description} width={isMobile ? 80 : (embedMedia ? 100 : 50)} classStyle={"tinySectionName"} />
                    )}
                    {(isPassionSlide(slide) || announcements) && isMobile && slide?.description && (
                        <div
                            className={clsx("tinySectionName")}
                            style={{ width: `${embedMedia ? 100 : 50}%` }}
                            dangerouslySetInnerHTML={{
                                __html: sanitizeHTML(slide.description),
                            }}
                        />
                    )}
                    {embedMedia?.speakerFullName && (
                        <div className={styles.speaker}>
                            <SectionName sectionName={embedMedia.speakerFullName} dynamic={false} />
                            <span>{embedMedia.videoLength}</span>
                        </div>
                    )}
                    {(() => {
                        const slideButton = slide?.button?.attributes ?? slide?.button;
                        return slideButton?.buttonName && slideButton?.buttonURL ? (
                            <CTABtn
                                btnName={slideButton.buttonName}
                                btnURL={embedMedia ? `/${slugBuilder("what we think", "CXA Hub", embedMedia?.title)}` : slideButton.buttonURL}
                                newTab={slideButton.newTab}
                            />
                        ) : null;
                    })()}
                </div>
                {embedMedia && !isMobile && (
                    <div className={styles.media}>
                        <VideoEmbed view="view" url={embedMedia.embedMedia} ctaRedirectURL={`/${slugBuilder("what we think", "CXA Hub", embedMedia?.title)}`} />
                    </div>
                )}
            </div>

            {children && <div className={styles['hero-children']}>{children}</div>}
            {!slide?.videoPresent && slide?.images?.length === 0 && (<div className={clsx(styles["blurred-img"], styles["full"], announcements && styles.announcementSection)}></div>)}
            {slide?.images?.length > 0 && (
                <div className={clsx(styles['hero-bg'],
                    { [styles["blurred-img"]]: !slide?.videoPresent },
                    (isPassionSlide(slide) || announcements) && styles.announcementSection
                    // {[styles["left"]]: lastDirection === "next"},
                    // {[styles["right"]]: lastDirection === "prev"},
                )}>
                    <Slider imageList={slide.images} mode='full' delay={5000} autoSign={false} />
                </div>
            )}
            {slide?.videoPresent && (<div className={styles["hero-bg-after"]}>
                <Slider imageList={slide.images} mode='full' delay={5000} autoSign={false} />
            </div>)}

        </div>
    );

    return (
        <div className={clsx(styles["hero-wrapper"])}>
            {renderSlide(slides[currentIndex], 0)}
            {slides.length > 1 && animating && renderSlide(slides[nextIndex])}
            {slides.length > 1 && <div className={styles["controls-wrapper"]}>
                <div className={styles["controls"]}>
                    <button onClick={() => handleManualChange("prev")} className={styles["control-btn"]}><GrPrevious size={22} color="white" /></button>
                    {slides.map((_, i) => <GoDotFill key={i} color={currentIndex === i ? "white" : "gray"} size={currentIndex === i ? 18 : 16} />)}
                    <button onClick={() => handleManualChange("next")} className={styles["control-btn"]}><GrNext size={22} color="white" /></button>
                </div>
            </div>}
        </div>
    );
};

export default Hero;
